import moment from "moment";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getInvoiceList } from "../../../redux/reducers/invoiceSlice";
import { Card, Badge, Button } from "react-bootstrap";
import { LuArrowUpRight, LuFileText, LuMail, LuArrowRight } from "react-icons/lu";
import { formatCurrency } from "../../../utils/currency";

export default function RecentInvoice() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeBusinessId = useSelector((state) => state.business.activeBusinessId);
  const invoicedata = useSelector((state) => state.invoice.invoiceList);
  const currency = useSelector((state) => state.business?.currencyContext?.currency || 'CAD');

  useEffect(() => {
    if (activeBusinessId) {
      dispatch(getInvoiceList({ businessId: activeBusinessId, page: 1, limit: 5, status: "" }));
    }
  }, [dispatch, activeBusinessId]);

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "paid": return <Badge bg="success-subtle" className="text-success rounded-pill px-2">Paid</Badge>;
      case "sent": return <Badge bg="info-subtle" className="text-info rounded-pill px-2">Sent</Badge>;
      case "draft": return <Badge bg="warning-subtle" className="text-warning rounded-pill px-2">Draft</Badge>;
      case "cancelled": return <Badge bg="danger-subtle" className="text-danger rounded-pill px-2">Cancelled</Badge>;
      default: return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-2">{status}</Badge>;
    }
  };

  const avatarColors = ["#28B0C4", "#6366f1", "#f59e0b", "#10b981", "#ef4444"];
  const getInitials = (name = "") => name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const getColor = (id = "") => avatarColors[id.charCodeAt(id.length - 1) % avatarColors.length];

  return (
    <Card className="border-0 shadow-sm rounded-4 h-100" style={{ padding: "18px 20px" }}>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="fw-bold text-dark" style={{ fontSize: "15px" }}>Recent Invoices</div>
          <div className="text-muted" style={{ fontSize: "11px" }}>Last 5 invoices</div>
        </div>
        <Button
          variant="light"
          size="sm"
          className="rounded-pill px-3 fw-semibold d-flex align-items-center gap-1"
          style={{ fontSize: "12px" }}
          onClick={() => navigate("/invoice")}
        >
          View All <LuArrowRight size={13} />
        </Button>
      </div>

      {/* Invoice rows */}
      <div className="d-flex flex-column">
        {invoicedata && invoicedata.length > 0 ? (
          invoicedata.map((invoice, idx) => (
            <div
              key={invoice._id}
              className={`d-flex align-items-center justify-content-between py-2 ${idx < invoicedata.length - 1 ? "border-bottom" : ""}`}
            >
              {/* Left — avatar + name */}
              <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                {invoice?.customer?.name ? (
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0"
                    style={{
                      width: 32,
                      height: 32,
                      fontSize: "11px",
                      backgroundColor: getColor(invoice._id),
                    }}
                  >
                    {getInitials(invoice.customer.name)}
                  </div>
                ) : (
                  <div
                    className="rounded-circle bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 32, height: 32 }}
                  >
                    <LuMail size={13} className="text-muted" />
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div className="fw-semibold text-dark text-truncate" style={{ fontSize: "13px", maxWidth: "160px" }}>
                    {invoice?.customer?.name || "Direct Payment"}
                  </div>
                  <div className="text-muted" style={{ fontSize: "10px" }}>
                    {moment(invoice?.invoiceDate || invoice?.createdAt).format("MMM DD, YYYY")}
                  </div>
                </div>
              </div>

              {/* Right — amount + status + action */}
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <div className="text-end">
                  <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                    {formatCurrency(invoice?.totalAmount || 0, currency)}
                  </div>
                  {getStatusBadge(invoice?.status)}
                </div>
                <Button
                  variant="light"
                  size="sm"
                  className="rounded-circle p-1 flex-shrink-0"
                  onClick={() => navigate(`/view-invoice/${invoice._id}`)}
                >
                  <LuArrowUpRight size={15} className="text-muted" />
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-4 text-muted">
            <LuFileText size={32} className="mb-2 opacity-25" />
            <p className="mb-0 small">No recent invoices</p>
          </div>
        )}
      </div>
    </Card>
  );
}